import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const YIL = new Date().getFullYear();
const AYLAR = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
               'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const n = (v: any) => {
  if (v === null || v === undefined || v === 0) return '-';
  return Number(v).toLocaleString('tr-TR');
};

// ── Navigation Stack Types ──
type NavItem =
  | { level: 'ozet' }
  | { level: 'buyukbas' }
  | { level: 'buyukbas_irk'; irk: string }
  | { level: 'kucukbas' }
  | { level: 'kucukbas_tur'; tur: string }
  | { level: 'at' }
  | { level: 'arazi' }
  | { level: 'tarla' }
  | { level: 'tarla_urun'; urun: string }
  | { level: 'yem' }
  | { level: 'bahce' }
  | { level: 'sut' }
  | { level: 'aricilik' }
  | { level: 'ceylan' }
  | { level: 'kopek' };

// ── Kart Bileşeni ──
function Kart({ baslik, ikon, renk, satirlar, onClick, buyuk }: {
  baslik: string; ikon: string; renk: string;
  satirlar: { etiket: string; deger: string }[];
  onClick?: () => void; buyuk?: boolean;
}) {
  const veriVar = satirlar.some(s => s.deger !== '-' && s.deger !== '0');
  return (
    <div onClick={onClick} style={{
      border: `1px solid ${renk}33`,
      borderRadius: 12, padding: buyuk ? '18px 20px' : '14px 16px',
      cursor: onClick ? 'pointer' : 'default',
      background: '#fff', transition: 'all .15s',
      boxShadow: '0 1px 4px rgba(0,0,0,.04)',
      borderLeft: `4px solid ${renk}`,
    }}
      onMouseEnter={e => { if (onClick) (e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = `0 4px 12px ${renk}22`); }}
      onMouseLeave={e => { if (onClick) (e.currentTarget.style.transform = 'none', e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.04)'); }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: buyuk ? 24 : 20 }}>{ikon}</span>
        <span style={{ fontWeight: 700, fontSize: buyuk ? 15 : 13, color: renk }}>{baslik}</span>
        {onClick && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#bbb' }}>detay &rarr;</span>}
      </div>
      {!veriVar ? (
        <div style={{ color: '#ccc', fontSize: 12, textAlign: 'center', padding: '4px 0' }}>Veri yok</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {satirlar.map(s => (
            <div key={s.etiket} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: '#777' }}>{s.etiket}</span>
              <span style={{ fontWeight: 600, color: '#333' }}>{s.deger}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Geri Butonu ──
function GeriButon({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 8,
      padding: '8px 14px', fontSize: 13, cursor: 'pointer',
      color: '#333', fontWeight: 500, marginBottom: 16,
      transition: 'all .1s',
    }}
      onMouseEnter={e => (e.currentTarget.style.background = '#e0e0e0')}
      onMouseLeave={e => (e.currentTarget.style.background = '#f0f0f0')}
    >
      <span style={{ fontSize: 16 }}>&larr;</span> {label}
    </button>
  );
}

export default function IsletmePano() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isletmeler, setIsletmeler] = useState<any>({ faal: [], kiralik: [], merkez: [] });
  const [seciliId, setSeciliId] = useState<number | null>(
    searchParams.get('isletme') ? Number(searchParams.get('isletme')) : null
  );
  const [yil, setYil] = useState(Number(searchParams.get('yil') || YIL));
  const [ay, setAy] = useState(Number(searchParams.get('ay') || 0));
  const initialDrill = searchParams.get('drill');
  const [ozet, setOzet] = useState<any>(null);
  const [tigemOzet, setTigemOzet] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sidebarAcik, setSidebarAcik] = useState(true);

  // Navigation stack - if drill param exists, start at that level
  const getInitialNav = (): NavItem[] => {
    if (initialDrill === 'buyukbas') return [{ level: 'ozet' }, { level: 'buyukbas' }];
    if (initialDrill === 'kucukbas') return [{ level: 'ozet' }, { level: 'kucukbas' }];
    if (initialDrill === 'at') return [{ level: 'ozet' }, { level: 'at' }];
    return [{ level: 'ozet' }];
  };
  const [navStack, setNavStack] = useState<NavItem[]>(getInitialNav());
  const [drillData, setDrillData] = useState<any>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const currentNav = navStack[navStack.length - 1];

  const pushNav = useCallback((item: NavItem) => {
    setDrillData(null);
    setDrillLoading(true);
    setNavStack(prev => [...prev, item]);
  }, []);

  const popNav = useCallback(() => {
    setNavStack(prev => {
      const newStack = prev.length > 1 ? prev.slice(0, -1) : prev;
      const newLevel = newStack[newStack.length - 1].level;
      if (newLevel === 'ozet') {
        setDrillData(null);
        setDrillLoading(false);
      } else {
        setDrillData(null);
        setDrillLoading(true);
      }
      return newStack;
    });
  }, []);

  // Reset nav when isletme changes
  useEffect(() => {
    setNavStack([{ level: 'ozet' }]);
    setDrillData(null);
  }, [seciliId]);

  // Isletmeleri yukle
  useEffect(() => {
    axios.get(`${API}/api/pano/isletmeler`, { headers: hdr() })
      .then(r => setIsletmeler(r.data))
      .catch(() => {});
  }, []);

  // TIGEM ozet yukle
  useEffect(() => {
    const params: any = { yil };
    if (ay > 0) params.ay = ay;
    axios.get(`${API}/api/pano/tigem-ozet`, { headers: hdr(), params })
      .then(r => setTigemOzet(r.data))
      .catch(() => setTigemOzet(null));
  }, [yil, ay]);

  // Isletme ozet yukle
  useEffect(() => {
    if (!seciliId) { setOzet(null); return; }
    setYukleniyor(true);
    const params: any = { yil };
    if (ay > 0) params.ay = ay;
    axios.get(`${API}/api/pano/isletme/${seciliId}`, { headers: hdr(), params })
      .then(r => setOzet(r.data))
      .catch(() => setOzet(null))
      .finally(() => setYukleniyor(false));
  }, [seciliId, yil, ay]);

  // Load drill data when nav changes (works for both isletme and tigem genel)
  useEffect(() => {
    const cur = navStack[navStack.length - 1];
    if (cur.level === 'ozet') { setDrillData(null); setDrillLoading(false); return; }

    setDrillLoading(true);
    let url = '';
    const base = seciliId ? `${API}/api/pano/isletme/${seciliId}` : `${API}/api/pano/tigem-ozet`;

    if (cur.level === 'buyukbas') {
      url = `${base}/buyukbas`;
    } else if (cur.level === 'buyukbas_irk') {
      url = `${base}/buyukbas/${encodeURIComponent(cur.irk)}`;
    } else if (cur.level === 'kucukbas') {
      url = `${base}/kucukbas`;
    } else if (cur.level === 'kucukbas_tur') {
      url = `${base}/kucukbas/${encodeURIComponent(cur.tur)}`;
    } else if (cur.level === 'at') {
      url = `${base}/at`;
    } else if (cur.level === 'arazi') {
      url = `${base}/arazi`;
    } else if (cur.level === 'tarla') {
      url = `${base}/tarla`;
    } else if (cur.level === 'tarla_urun') {
      url = `${base}/tarla/${encodeURIComponent(cur.urun)}`;
    } else if (cur.level === 'yem') {
      url = `${base}/yem`;
    } else if (cur.level === 'bahce') {
      url = `${base}/bahce`;
    } else if (cur.level === 'sut') {
      url = `${base}/sut`;
    } else if (cur.level === 'aricilik') {
      url = `${base}/aricilik`;
    } else if (cur.level === 'ceylan') {
      url = `${base}/ceylan`;
    } else if (cur.level === 'kopek') {
      url = `${base}/kopek`;
    }

    if (url) {
      const params: any = { yil };
      if (ay > 0) params.ay = ay;
      axios.get(url, { headers: hdr(), params })
        .then(r => setDrillData(r.data))
        .catch(() => setDrillData(null))
        .finally(() => setDrillLoading(false));
    } else {
      setDrillLoading(false);
    }
  }, [navStack, seciliId, yil, ay]);

  const secIsletme = (id: number | null) => {
    setSeciliId(id);
    const p: Record<string, string> = { yil: String(yil) };
    if (ay > 0) p.ay = String(ay);
    if (id) { p.isletme = String(id); setSearchParams(p); }
    else setSearchParams(p);
  };

  const seciliIsletme = ozet?.isletme;

  // ── Breadcrumb (ortak) ──
  const buildBreadcrumbLabels = (rootLabel: string) => {
    const labels: string[] = [];
    for (const item of navStack) {
      if (item.level === 'ozet') labels.push(rootLabel);
      else if (item.level === 'buyukbas') labels.push('Buyukbas');
      else if (item.level === 'buyukbas_irk') labels.push(item.irk);
      else if (item.level === 'kucukbas') labels.push('Kucukbas');
      else if (item.level === 'kucukbas_tur') labels.push(item.tur);
      else if (item.level === 'at') labels.push('At');
      else if (item.level === 'arazi') labels.push('Arazi');
      else if (item.level === 'tarla') labels.push('Tarla Bitkileri');
      else if (item.level === 'tarla_urun') labels.push(item.urun);
      else if (item.level === 'yem') labels.push('Yem Bitkileri');
      else if (item.level === 'bahce') labels.push('Bahce');
      else if (item.level === 'sut') labels.push('Sut Uretimi');
      else if (item.level === 'aricilik') labels.push('Aricilik');
      else if (item.level === 'ceylan') labels.push('Ceylan');
      else if (item.level === 'kopek') labels.push('Coban Kopegi');
    }
    return labels;
  };

  const renderBreadcrumbUI = (labels: string[]) => {
    if (labels.length <= 1) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12, fontSize: 12, color: '#888', flexWrap: 'wrap' }}>
        {labels.map((lbl, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <span style={{ color: '#ccc' }}>/</span>}
            <span style={{ color: i === labels.length - 1 ? '#1a5276' : '#888', fontWeight: i === labels.length - 1 ? 600 : 400 }}>{lbl}</span>
          </span>
        ))}
      </div>
    );
  };

  const renderBreadcrumb = () => renderBreadcrumbUI(buildBreadcrumbLabels(seciliIsletme?.ad || 'Ozet'));
  const renderBreadcrumbTigem = () => renderBreadcrumbUI(buildBreadcrumbLabels('TIGEM Genel'));

  // ── Ortak grup başlık ──
  const GrupBaslik = ({ baslik }: { baslik: string }) => (
    <div style={{ fontSize: 13, fontWeight: 700, color: '#444', margin: '16px 0 8px',
      borderBottom: '2px solid #e8e8e8', paddingBottom: 6 }}>{baslik}</div>
  );

  const Grid = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 4 }}>
      {children}
    </div>
  );

  // ── ORTAK: Standart grup kartları (hem işletme hem TİGEM için) ──
  const renderGruplar = (o: any) => (
    <div>
      <GrupBaslik baslik="🐄 Hayvancılık" />
      <Grid>
        <Kart baslik="Büyükbaş" ikon="🐄" renk="#1e8449" buyuk
          onClick={() => pushNav({ level: 'buyukbas' })}
          satirlar={[
            { etiket: 'Toplam Hayvan', deger: n(o.buyukbas?.toplam_hayvan) + ' baş' },
            { etiket: 'İnek', deger: n(o.buyukbas?.toplam_inek) + ' baş' },
            { etiket: 'Buzağı Doğum', deger: n(o.buyukbas?.toplam_buzagi) + ' baş' },
            { etiket: 'Satış', deger: n(o.buyukbas?.toplam_satis) + ' baş' },
          ]} />
        <Kart baslik="Küçükbaş" ikon="🐑" renk="#1a5276" buyuk
          onClick={() => pushNav({ level: 'kucukbas' })}
          satirlar={[
            { etiket: 'Toplam Hayvan', deger: n(o.kucukbas?.toplam_hayvan) + ' baş' },
            { etiket: 'Ana Hayvan', deger: n(o.kucukbas?.toplam_ana) + ' baş' },
            { etiket: 'Doğum', deger: n(o.kucukbas?.toplam_dogum) + ' baş' },
            { etiket: 'Süt', deger: n(o.kucukbas?.toplam_sut_kg) + ' kg' },
          ]} />
        <Kart baslik="At Yetiştiriciliği" ikon="🐎" renk="#6c3483" buyuk
          onClick={() => pushNav({ level: 'at' })}
          satirlar={[
            { etiket: 'Toplam At', deger: n(o.at?.toplam_at) + ' baş' },
            { etiket: 'Satış', deger: n(o.at?.satis_toplam) + ' baş' },
            { etiket: 'Doğum', deger: n(o.at?.dogum_sayisi) + ' baş' },
          ]} />
        <Kart baslik="Asya Ceylanı" ikon="🦌" renk="#7d6608"
          onClick={() => pushNav({ level: 'ceylan' })}
          satirlar={[
            { etiket: 'Toplam', deger: n(o.ceylan?.toplam_bas) + ' baş' },
            { etiket: 'Doğum', deger: n(o.ceylan?.dogum_sayisi) },
            { etiket: 'Satış', deger: n(o.ceylan?.satis_sayisi) },
          ]} />
        <Kart baslik="Çoban Köpeği" ikon="🐕" renk="#935116"
          onClick={() => pushNav({ level: 'kopek' })}
          satirlar={[
            { etiket: 'Toplam', deger: n(o.kopek?.toplam_bas) + ' baş' },
            { etiket: 'Doğum', deger: n(o.kopek?.dogum_sayisi) },
            { etiket: 'Satış', deger: n(o.kopek?.satis_toplam) },
          ]} />
        <Kart baslik="Arıcılık" ikon="🐝" renk="#d4ac0d"
          onClick={() => pushNav({ level: 'aricilik' })}
          satirlar={[
            { etiket: 'Kovan', deger: n(o.aricilik?.toplam_kovan) },
            { etiket: 'Bal Üretimi', deger: n(o.aricilik?.toplam_bal_kg) + ' kg' },
          ]} />
        <Kart baslik="Süt Üretimi" ikon="🥛" renk="#3478c6"
          onClick={() => pushNav({ level: 'sut' })}
          satirlar={[
            { etiket: 'Yıllık Toplam', deger: n(o.sut?.yillik_sut_kg) + ' kg' },
            { etiket: 'Günlük Ort.', deger: n(o.sut?.gunluk_ortalama) + ' kg' },
          ]} />
      </Grid>

      <GrupBaslik baslik="🌾 Üretim Verileri" />
      <Grid>
        <Kart baslik="Tarla Bitkileri" ikon="🌾" renk="#7d6608"
          onClick={() => pushNav({ level: 'tarla' })}
          satirlar={[
            { etiket: 'Ekim Alanı', deger: n(o.tarla?.toplam_ekim_da) + ' da' },
            { etiket: 'Üretim', deger: n(o.tarla?.toplam_uretim_kg) + ' kg' },
          ]} />
        <Kart baslik="Yem Bitkileri" ikon="🌿" renk="#27ae60"
          onClick={() => pushNav({ level: 'yem' })}
          satirlar={[
            { etiket: 'Ekim Alanı', deger: n(o.yem?.toplam_alan_da) + ' da' },
            { etiket: 'Üretim', deger: n(o.yem?.toplam_uretim_kg) + ' kg' },
          ]} />
        <Kart baslik="Bahçe Kültürleri" ikon="🌳" renk="#e67e22"
          onClick={() => pushNav({ level: 'bahce' })}
          satirlar={[
            { etiket: 'Alan', deger: n(o.bahce?.toplam_alan_da) + ' da' },
            { etiket: 'Üretim', deger: n(o.bahce?.toplam_uretim_kg) + ' kg' },
          ]} />
      </Grid>

      <GrupBaslik baslik="🏗️ Varlık Yönetimi" />
      <Grid>
        <Kart baslik="Arazi" ikon="🗺️" renk="#117a65"
          onClick={() => pushNav({ level: 'arazi' })}
          satirlar={[
            { etiket: 'Toplam Arazi', deger: n(o.arazi?.toplam_arazi_da) + ' da' },
            { etiket: 'Tarla', deger: n(o.arazi?.tarla_arazisi_da) + ' da' },
            { etiket: 'Sulanan', deger: n(o.arazi?.sulanan_arazi_da) + ' da' },
          ]} />
        <Kart baslik="Makine & Ekipman" ikon="🚜" renk="#1a5276"
          satirlar={[
            { etiket: 'Traktör', deger: n(o.makine?.traktor_adet) + ' adet' },
            { etiket: 'Traktör Güç', deger: n(o.makine?.traktor_guc_kw) + ' KW' },
            { etiket: 'Diğer Makine', deger: n(o.makine?.diger_makine_adet) + ' adet' },
          ]} />
        <Kart baslik="Taşıt" ikon="🚗" renk="#6c3483"
          satirlar={[
            { etiket: 'Toplam Taşıt', deger: n(o.tasit?.toplam_tasit) + ' adet' },
          ]} />
        <Kart baslik="Tesis & Altyapı" ikon="🏭" renk="#7d6608"
          satirlar={[
            { etiket: 'Silo Kapasitesi', deger: n(o.tesis?.silo_ton) + ' ton' },
            { etiket: 'BB Barınak', deger: n(o.tesis?.buyukbas_barinagi_bas) + ' baş' },
          ]} />
      </Grid>
    </div>
  );

  const renderOzetKartlari = () => {
    if (!ozet) return null;
    return renderGruplar(ozet);
  };

  // ── SEVIYE: Buyukbas irk kartlari ──
  const renderBuyukbasIrklar = () => {
    if (!drillData?.irk_dagilimi || drillData.irk_dagilimi.length === 0) return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Buyukbas irk verisi bulunamadi.</div>
      </div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e8449', margin: '0 0 12px' }}>🐄 Buyukbas - Irk Dagilimi</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {drillData.irk_dagilimi.map((d: any) => (
            <Kart key={d.irk} baslik={d.irk} ikon="🐄" renk="#1e8449" buyuk
              onClick={() => pushNav({ level: 'buyukbas_irk', irk: d.irk })}
              satirlar={[
                { etiket: 'Toplam', deger: n(d.toplam_hayvan) + ' bas' },
                { etiket: 'Inek', deger: n(d.inek) + ' bas' },
                { etiket: 'Boga', deger: n(d.boga) + ' bas' },
                { etiket: 'Buzagi Dogum', deger: n(d.buzagi_dogum) + ' bas' },
                { etiket: 'Satis', deger: n(d.satis) + ' bas' },
              ]} />
          ))}
        </div>

        {/* Aylik ozet */}
        {drillData.aylik && drillData.aylik.some((a: any) => a.toplam_hayvan > 0) && (
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#555', margin: '0 0 10px' }}>📅 Aylik Toplam</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {drillData.aylik.filter((a: any) => a.toplam_hayvan > 0).map((a: any) => (
                <div key={a.ay} style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 12px', border: '1px solid #eee' }}>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{a.ay_ad}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1e8449' }}>{n(a.toplam_hayvan)} bas</div>
                  <div style={{ fontSize: 11, color: '#666' }}>Inek: {n(a.inek)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── SEVIYE: Buyukbas tek irk detay (aylik) ──
  const renderBuyukbasIrkDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div>
        <GeriButon onClick={popNav} label="Irklara Don" />
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Bu irk icin aylik veri bulunamadi.</div>
      </div>
    );
    const irk = (currentNav as any).irk;
    return (
      <div>
        <GeriButon onClick={popNav} label="Irklara Don" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e8449', margin: '0 0 12px' }}>🐄 {irk} - Aylik Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#1e8449"
              satirlar={[
                { etiket: 'Toplam', deger: n(a.toplam_hayvan) + ' bas' },
                { etiket: 'Inek', deger: n(a.inek) + ' bas' },
                { etiket: 'Boga', deger: n(a.boga) + ' bas' },
                { etiket: 'Duve', deger: n(a.duve) + ' bas' },
                { etiket: 'Dana', deger: n(a.dana) + ' bas' },
                { etiket: 'Buzagi E/D', deger: `${n(a.buzagi_erkek)} / ${n(a.buzagi_disi)}` },
                { etiket: 'Dogum', deger: n(a.buzagi_dogum) + ' bas' },
                { etiket: 'Damizlik Satis', deger: n(a.damizlik_satis) },
                { etiket: 'Besi Satis', deger: n(a.besi_satis) },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Kucukbas tur kartlari ──
  const renderKucukbasTurler = () => {
    if (!drillData?.tur_dagilimi || drillData.tur_dagilimi.length === 0) return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Kucukbas tur verisi bulunamadi.</div>
      </div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a5276', margin: '0 0 12px' }}>🐑 Kucukbas - Tur Dagilimi</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {drillData.tur_dagilimi.map((d: any) => (
            <Kart key={d.tur} baslik={d.tur} ikon={d.tur === 'Keci' ? '🐐' : '🐑'} renk="#1a5276" buyuk
              onClick={() => pushNav({ level: 'kucukbas_tur', tur: d.tur })}
              satirlar={[
                { etiket: 'Toplam', deger: n(d.toplam_hayvan) + ' bas' },
                { etiket: 'Ana', deger: n(d.ana) + ' bas' },
                { etiket: 'Toklu', deger: n(d.toklu) + ' bas' },
                { etiket: 'Kuzu/Oglak', deger: n(d.kuzu) + ' bas' },
                { etiket: 'Dogum', deger: n(d.dogum) + ' bas' },
                { etiket: 'Sut', deger: n(d.sut_kg) + ' kg' },
              ]} />
          ))}
        </div>

        {/* Aylik ozet */}
        {drillData.aylik && drillData.aylik.some((a: any) => a.toplam_hayvan > 0) && (
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#555', margin: '0 0 10px' }}>📅 Aylik Toplam</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {drillData.aylik.filter((a: any) => a.toplam_hayvan > 0).map((a: any) => (
                <div key={a.ay} style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 12px', border: '1px solid #eee' }}>
                  <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{a.ay_ad}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a5276' }}>{n(a.toplam_hayvan)} bas</div>
                  <div style={{ fontSize: 11, color: '#666' }}>Ana: {n(a.ana)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── SEVIYE: Kucukbas tek tur detay (aylik) ──
  const renderKucukbasTurDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div>
        <GeriButon onClick={popNav} label="Turlere Don" />
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Bu tur icin aylik veri bulunamadi.</div>
      </div>
    );
    const tur = (currentNav as any).tur;
    return (
      <div>
        <GeriButon onClick={popNav} label="Turlere Don" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a5276', margin: '0 0 12px' }}>
          {tur === 'Keci' ? '🐐' : '🐑'} {tur} - Aylik Detay
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#1a5276"
              satirlar={[
                { etiket: 'Toplam', deger: n(a.toplam_hayvan) + ' bas' },
                { etiket: 'Ana', deger: n(a.ana) + ' bas' },
                { etiket: 'Koc/Teke', deger: n(a.koc_teke) + ' bas' },
                { etiket: 'E. Toklu', deger: n(a.erkek_toklu) + ' bas' },
                { etiket: 'D. Toklu', deger: n(a.disi_toklu) + ' bas' },
                { etiket: 'E. Kuzu', deger: n(a.erkek_kuzu) + ' bas' },
                { etiket: 'D. Kuzu', deger: n(a.disi_kuzu) + ' bas' },
                { etiket: 'Dogum', deger: n(a.dogum) + ' bas' },
                { etiket: 'Sut', deger: n(a.sut_kg) + ' kg' },
                { etiket: 'Satis', deger: n(a.damizlik_satis) },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: At aylik detay ──
  const renderAtDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>At verisi bulunamadi.</div>
      </div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#6c3483', margin: '0 0 12px' }}>🐎 At - Aylik Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#6c3483"
              satirlar={[
                { etiket: 'Toplam', deger: n(a.toplam_at) + ' bas' },
                { etiket: 'Aygir', deger: n(a.aygir) },
                { etiket: 'Kisrak', deger: n(a.kisrak) },
                { etiket: '3 Tay E/D', deger: `${n(a.tay3_erkek)} / ${n(a.tay3_disi)}` },
                { etiket: '2 Tay E/D', deger: `${n(a.tay2_erkek)} / ${n(a.tay2_disi)}` },
                { etiket: '1 Tay E/D', deger: `${n(a.tay1_erkek)} / ${n(a.tay1_disi)}` },
                { etiket: 'Sut Tay E/D', deger: `${n(a.sut_tay_erkek)} / ${n(a.sut_tay_disi)}` },
                { etiket: 'Satis', deger: n(a.satis_toplam) },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Arazi detay ──
  const renderAraziDetay = () => {
    if (!drillData?.arazi_kullanimi) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Arazi verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#117a65', margin: '0 0 12px' }}>🗺️ Arazi Detay — Toplam: {n(drillData.toplam_arazi_da)} da</h3>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#555', margin: '8px 0' }}>Arazi Kullanimi</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
          {drillData.arazi_kullanimi.filter((a: any) => a.deger > 0).map((a: any) => (
            <Kart key={a.alan} baslik={a.alan} ikon="🌍" renk="#117a65" satirlar={[{ etiket: 'Alan', deger: n(a.deger) + ' da' }]} />
          ))}
        </div>
        {drillData.sulama?.some((s: any) => s.deger > 0) && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', margin: '8px 0' }}>Sulama Sistemi</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {drillData.sulama.filter((s: any) => s.deger > 0).map((s: any) => (
                <Kart key={s.alan} baslik={s.alan} ikon="💧" renk="#2980b9" satirlar={[{ etiket: 'Alan', deger: n(s.deger) + ' da' }]} />
              ))}
            </div>
          </>
        )}
        {drillData.debi_lt_sn > 0 && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#555' }}>Toplam Debi: <strong>{n(drillData.debi_lt_sn)} lt/sn</strong></div>
        )}
      </div>
    );
  };

  // ── SEVIYE: Tarla urun kartlari ──
  const renderTarlaUrunler = () => {
    if (!drillData?.urun_dagilimi || drillData.urun_dagilimi.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Tarla bitkileri verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7d6608', margin: '0 0 12px' }}>🌾 Tarla Bitkileri - Urun Dagilimi</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.urun_dagilimi.map((d: any) => (
            <Kart key={d.urun} baslik={d.urun} ikon="🌾" renk="#7d6608" buyuk
              onClick={() => pushNav({ level: 'tarla_urun', urun: d.urun })}
              satirlar={[
                { etiket: 'Ekim', deger: n(d.ekim_alani_da) + ' da' },
                { etiket: 'Uretim', deger: n(Math.round(d.uretim_kg)) + ' kg' },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Tarla tek urun cesit detay ──
  const renderTarlaUrunDetay = () => {
    if (!drillData?.cesit_dagilimi || drillData.cesit_dagilimi.length === 0) return (
      <div><GeriButon onClick={popNav} label="Urunlere Don" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Cesit verisi bulunamadi.</div></div>
    );
    const urun = (currentNav as any).urun;
    return (
      <div>
        <GeriButon onClick={popNav} label="Urunlere Don" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7d6608', margin: '0 0 12px' }}>🌾 {urun} - Cesit Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.cesit_dagilimi.map((c: any) => (
            <Kart key={c.cesit} baslik={c.cesit} ikon="🌱" renk="#7d6608"
              satirlar={[
                { etiket: 'Ekim', deger: n(c.ekim_alani_da) + ' da' },
                { etiket: 'Uretim', deger: n(Math.round(c.uretim_kg)) + ' kg' },
                { etiket: 'Verim', deger: n(c.verim_kg_da) + ' kg/da' },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Yem urun kartlari ──
  const renderYemUrunler = () => {
    if (!drillData?.urun_dagilimi || drillData.urun_dagilimi.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Yem bitkileri verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#27ae60', margin: '0 0 12px' }}>🌿 Yem Bitkileri - Urun Dagilimi</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.urun_dagilimi.map((d: any) => (
            <Kart key={d.urun} baslik={d.urun} ikon="🌿" renk="#27ae60"
              satirlar={[
                { etiket: 'Alan', deger: n(d.alan_da) + ' da' },
                { etiket: 'Uretim', deger: n(Math.round(d.uretim_kg)) + ' kg' },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Bahce urun kartlari ──
  const renderBahceUrunler = () => {
    if (!drillData?.urun_dagilimi || drillData.urun_dagilimi.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Bahce verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#e67e22', margin: '0 0 12px' }}>🌳 Bahce - Urun Dagilimi</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.urun_dagilimi.map((d: any) => (
            <Kart key={d.urun} baslik={d.urun} ikon="🌳" renk="#e67e22"
              satirlar={[
                { etiket: 'Alan', deger: n(d.alan_da) + ' da' },
                { etiket: 'Uretim', deger: n(Math.round(d.uretim_kg)) + ' kg' },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Sut aylik kartlar ──
  const renderSutDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Sut uretim verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#3478c6', margin: '0 0 12px' }}>🥛 Sut Uretimi - Aylik Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#3478c6"
              satirlar={[
                { etiket: 'Sagmal Inek', deger: n(a.sagmal_inek) + ' bas' },
                { etiket: 'Gunluk Sut', deger: n(a.gunluk_sut_kg) + ' kg' },
                { etiket: 'Ort/Inek', deger: n(a.sagmal_ortalama) + ' kg' },
                { etiket: 'Yillik Top.', deger: n(a.yillik_sut_kg) + ' kg' },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Aricilik aylik kartlar ──
  const renderAricilikDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aricilik verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#d4ac0d', margin: '0 0 12px' }}>🐝 Aricilik - Aylik Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#d4ac0d"
              satirlar={[
                { etiket: 'Kovan', deger: n(a.kovan_sayisi) },
                { etiket: 'Bal', deger: n(a.bal_uretimi_kg) + ' kg' },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Ceylan aylik kartlar ──
  const renderCeylanDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Ceylan verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7d6608', margin: '0 0 12px' }}>🦌 Asya Ceylani - Aylik Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#7d6608"
              satirlar={[
                { etiket: 'Toplam', deger: n(a.toplam_bas) + ' bas' },
                { etiket: 'Erkek', deger: n(a.erkek) },
                { etiket: 'Disi', deger: n(a.disi) },
                { etiket: 'Yavru', deger: n(a.yavru) },
                { etiket: 'Dogum', deger: n(a.dogum) },
                { etiket: 'Olum', deger: n(a.olum) },
                { etiket: 'Satis', deger: n(a.satis) },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── SEVIYE: Kopek aylik kartlar ──
  const renderKopekDetay = () => {
    if (!drillData?.aylik || drillData.aylik.length === 0) return (
      <div><GeriButon onClick={popNav} label="Geri" /><div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Kopek verisi bulunamadi.</div></div>
    );
    return (
      <div>
        <GeriButon onClick={popNav} label="Geri" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#935116', margin: '0 0 12px' }}>🐕 Coban Kopegi - Aylik Detay</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {drillData.aylik.map((a: any) => (
            <Kart key={a.ay} baslik={a.ay_ad} ikon="📅" renk="#935116"
              satirlar={[
                { etiket: 'Toplam', deger: n(a.toplam_bas) + ' bas' },
                { etiket: 'Erkek', deger: n(a.erkek) },
                { etiket: 'Disi', deger: n(a.disi) },
                { etiket: 'Yavru', deger: n(a.yavru) },
                { etiket: 'Dogum', deger: n(a.dogum) },
                { etiket: 'Olum', deger: n(a.olum) },
                { etiket: 'Satis', deger: n(a.satis) },
              ]} />
          ))}
        </div>
      </div>
    );
  };

  // ── TIGEM genel goruntuleme (ayni drill-down mantigi) ──
  const renderTigemOzet = () => {
    if (!tigemOzet) return null;
    const t = tigemOzet;

    // Eger drill seviyesindeyse, ayni renderlar calisir (API URL'leri tigem-ozet prefix'li)
    if (currentNav.level !== 'ozet') {
      const drillContent = () => {
        if (drillLoading) return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Yukleniyor...</div>;
        if (!drillData) return (
          <div>
            <GeriButon onClick={popNav} label="Geri" />
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Veri bulunamadi.</div>
          </div>
        );
        if (currentNav.level === 'buyukbas') return renderBuyukbasIrklar();
        if (currentNav.level === 'buyukbas_irk') return renderBuyukbasIrkDetay();
        if (currentNav.level === 'kucukbas') return renderKucukbasTurler();
        if (currentNav.level === 'kucukbas_tur') return renderKucukbasTurDetay();
        if (currentNav.level === 'at') return renderAtDetay();
        if (currentNav.level === 'arazi') return renderAraziDetay();
        if (currentNav.level === 'tarla') return renderTarlaUrunler();
        if (currentNav.level === 'tarla_urun') return renderTarlaUrunDetay();
        if (currentNav.level === 'yem') return renderYemUrunler();
        if (currentNav.level === 'bahce') return renderBahceUrunler();
        if (currentNav.level === 'sut') return renderSutDetay();
        if (currentNav.level === 'aricilik') return renderAricilikDetay();
        if (currentNav.level === 'ceylan') return renderCeylanDetay();
        if (currentNav.level === 'kopek') return renderKopekDetay();
        return null;
      };
      return (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a5276', margin: 0 }}>TİGEM Genel Özet</h2>
            <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
              {ay > 0 ? `${yil} / ${AYLAR[ay]} — Tüm işletmeler` : `${yil} yılı — Tüm işletmeler toplamı`}
            </p>
          </div>
          {renderBreadcrumbTigem()}
          {drillContent()}
        </div>
      );
    }

    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a5276', margin: 0 }}>TİGEM Genel Özet</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            {ay > 0
              ? `${yil} / ${AYLAR[ay]} — Tüm işletmeler`
              : `${yil} yılı — İşletme başına referans dönem verisi`}
          </p>
        </div>
        {renderGruplar(t)}
      </div>
    );
  };

  // ── Isletme icerik (nav stack'e gore) ──
  const renderIsletmeIcerik = () => {
    if (yukleniyor) return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Yukleniyor...</div>;
    if (!ozet) return null;

    const refDonem = ozet?.referans_donem;

    // Baslik her zaman goster
    const baslikBlock = (
      <div style={{
        background: 'linear-gradient(135deg, #1a5276, #2c3e50)', borderRadius: 14,
        padding: '18px 22px', color: '#fff', marginBottom: 16
      }}>
        <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: '0.05em' }}>
          {seciliIsletme?.tip} &middot; {seciliIsletme?.il}{seciliIsletme?.ilce ? ' / ' + seciliIsletme.ilce : ''}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{seciliIsletme?.ad}</div>
        {seciliIsletme?.mudur && <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>Mudur: {seciliIsletme.mudur}</div>}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          {refDonem?.ay_ad ? (
            <span style={{
              background: 'rgba(255,255,255,0.15)', borderRadius: 20,
              padding: '2px 12px', fontSize: 12, fontWeight: 600
            }}>
              📅 {refDonem.yil} / {refDonem.ay_ad} verisi
            </span>
          ) : refDonem?.uyari ? (
            <span style={{
              background: 'rgba(255,200,0,0.25)', borderRadius: 20,
              padding: '2px 12px', fontSize: 12, color: '#ffe082'
            }}>
              ⚠️ {refDonem.uyari}
            </span>
          ) : null}
        </div>
      </div>
    );

    const drillContent = () => {
      if (currentNav.level === 'ozet') return renderOzetKartlari();
      if (drillLoading) return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Yukleniyor...</div>;
      if (!drillData) return (
        <div>
          <GeriButon onClick={popNav} label="Geri" />
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Bu isletme icin veri bulunamadi.</div>
        </div>
      );
      if (currentNav.level === 'buyukbas') return renderBuyukbasIrklar();
      if (currentNav.level === 'buyukbas_irk') return renderBuyukbasIrkDetay();
      if (currentNav.level === 'kucukbas') return renderKucukbasTurler();
      if (currentNav.level === 'kucukbas_tur') return renderKucukbasTurDetay();
      if (currentNav.level === 'at') return renderAtDetay();
      if (currentNav.level === 'arazi') return renderAraziDetay();
      if (currentNav.level === 'tarla') return renderTarlaUrunler();
      if (currentNav.level === 'tarla_urun') return renderTarlaUrunDetay();
      if (currentNav.level === 'yem') return renderYemUrunler();
      if (currentNav.level === 'bahce') return renderBahceUrunler();
      if (currentNav.level === 'sut') return renderSutDetay();
      if (currentNav.level === 'aricilik') return renderAricilikDetay();
      if (currentNav.level === 'ceylan') return renderCeylanDetay();
      if (currentNav.level === 'kopek') return renderKopekDetay();
      return null;
    };

    return (
      <div>
        {baslikBlock}
        {renderBreadcrumb()}
        {drillContent()}
      </div>
    );
  };

  // ── Sidebar ──
  const renderSidebar = () => (
    <div style={{
      width: sidebarAcik ? 240 : 0, minWidth: sidebarAcik ? 240 : 0,
      borderRight: sidebarAcik ? '1px solid #e0e0e0' : 'none',
      background: '#f8f9fa', overflowY: 'auto', overflowX: 'hidden',
      transition: 'all .2s', height: 'calc(100vh - 120px)',
      flexShrink: 0,
    }}>
      {sidebarAcik && (
        <div style={{ padding: 8 }}>
          {/* TIGEM */}
          <div onClick={() => secIsletme(null)} style={{
            padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
            background: !seciliId ? '#1a5276' : 'transparent',
            color: !seciliId ? '#fff' : '#1a5276', fontWeight: 700, fontSize: 13,
            transition: 'all .15s',
          }}>
            🏛️ TIGEM Genel
          </div>

          {/* Faal */}
          {isletmeler.faal.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#2e7d32', padding: '10px 12px 4px', letterSpacing: '0.04em' }}>
                FAAL ISLETMELER ({isletmeler.faal.length})
              </div>
              {isletmeler.faal.map((i: any) => (
                <div key={i.id} onClick={() => secIsletme(i.id)} style={{
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                  background: seciliId === i.id ? '#1a5276' : 'transparent',
                  color: seciliId === i.id ? '#fff' : '#333',
                  fontWeight: seciliId === i.id ? 600 : 400,
                  transition: 'all .1s', marginBottom: 1,
                }}>
                  <strong>{i.kod}</strong> {i.ad.replace(' Tarim Isletmesi', '').replace(' Tarım İşletmesi', '')}
                </div>
              ))}
            </>
          )}

          {/* Kiralik */}
          {isletmeler.kiralik.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#e65100', padding: '10px 12px 4px', letterSpacing: '0.04em' }}>
                KIRALIK ISLETMELER ({isletmeler.kiralik.length})
              </div>
              {isletmeler.kiralik.map((i: any) => (
                <div key={i.id} onClick={() => secIsletme(i.id)} style={{
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                  background: seciliId === i.id ? '#1a5276' : 'transparent',
                  color: seciliId === i.id ? '#fff' : '#777',
                  fontWeight: seciliId === i.id ? 600 : 400,
                  transition: 'all .1s', marginBottom: 1,
                }}>
                  <strong>{i.kod}</strong> {i.ad.replace(' Tarim Isletmesi', '').replace(' Tarım İşletmesi', '')}
                </div>
              ))}
            </>
          )}

          {/* Merkez */}
          {isletmeler.merkez.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#1565c0', padding: '10px 12px 4px', letterSpacing: '0.04em' }}>
                MERKEZ ({isletmeler.merkez.length})
              </div>
              {isletmeler.merkez.map((i: any) => (
                <div key={i.id} onClick={() => secIsletme(i.id)} style={{
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                  background: seciliId === i.id ? '#1a5276' : 'transparent',
                  color: seciliId === i.id ? '#fff' : '#555',
                  fontWeight: seciliId === i.id ? 600 : 400,
                  transition: 'all .1s', marginBottom: 1,
                }}>
                  <strong>{i.kod}</strong> {i.ad}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1>🏢 Isletme Panosu</h1>
          <p style={{ color: '#888', fontSize: 13 }}>Isletme bazinda tum verilerin ozet goruntulenmesi</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn" style={{ padding: '6px 12px', fontSize: 12 }}
            onClick={() => setSidebarAcik(!sidebarAcik)}>
            {sidebarAcik ? '◀ Gizle' : '▶ İşletmeler'}
          </button>
          <input type="number" className="form-control" style={{ width: 90, fontSize: 13 }} value={yil}
            onChange={e => setYil(Number(e.target.value))} />
          <select className="form-control" style={{ width: 120, fontSize: 13 }} value={ay}
            onChange={e => setAy(Number(e.target.value))}>
            <option value={0}>Tüm Yıl</option>
            {AYLAR.slice(1).map((ad, i) => <option key={i + 1} value={i + 1}>{ad}</option>)}
          </select>
          <span style={{ fontSize: 12, color: '#888' }}>
            {ay > 0 ? `${yil} / ${AYLAR[ay]}` : `${yil} yılı`}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0 }}>
        {renderSidebar()}
        <div style={{ flex: 1, padding: '0 16px 40px', overflowY: 'auto' }}>
          {!seciliId ? renderTigemOzet() : renderIsletmeIcerik()}
        </div>
      </div>
    </div>
  );
}
